import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBoxes, FaRoute } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import { useUserPdvs } from '../../../../hooks/useUserPdvs';
import { useAllPdvs } from '../../../../hooks/useAllPdvs';
import { createTransferOrder } from '../../../../services/inventory/transfersApi';
import { notifyApiError } from '../../../../utils/feedback';

import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../../styles/layout/PageHeader';
import {
   AsideActions,
   AsideCard,
   AsideChecklist,
   AsideRow,
   ComposerPageGrid,
   Field,
   FieldError,
   FieldHint,
   FieldLabel,
   FieldSelect,
   FieldTextarea,
   FormCard,
   FormGrid,
   Section,
   SectionHead,
   SectionIcon,
   SectionTitles,
} from '../../../../styles/ui/Composer';
import {
   GhostButton,
   GhostOnDarkButton,
   PrimaryButton,
} from '../../../../styles/ui/Button';

import CheckLine from '../components/CheckLine';
import TransferItemsEditor from '../components/TransferItemsEditor';
import { useOriginAvailability } from '../hooks/useOriginAvailability';

// ---------------------------------------------------------------------------
// Criação de ordem de transferência (POST /transfer-orders).
//
// Passo 1 dos dois: aqui NADA sai fisicamente — o pedido apenas RESERVA o
// estoque na origem (disponível = físico − reservado). A saída acontece na
// expedição, feita depois, no detalhe da ordem.
//
// Refatoração visual 2026-09-10: a tela usa os primitivos "composer" de
// `styles/ui/Composer.js` (promovidos do Routing nesta sessão — fecha o desvio
// §7.2 do doc do módulo). Layout em duas colunas: FormCard seccionado
// (① Rota ② Itens) + aside "Resumo" sticky com checklist e as ações.
//
// Escopo dos selects (regra do backend, espelhada na UI):
//   · ORIGEM  = só PDVs do usuário (`/users/me/pdvs`) — expedir exige acesso à
//     origem, então oferecer outra coisa aqui seria convidar ao 403;
//   · DESTINO = catálogo completo (`/pdvs`), menos a própria origem.
//
// "Disponível na origem"/"Sobra" (hook useOriginAvailability) são
// INFORMATIVOS — decisão D3: o backend permite reservar acima do disponível,
// então a UI avisa (⚠ no checklist) e NÃO bloqueia o Criar. A carga é
// fail-open: falhou → colunas somem, nada trava.
// ---------------------------------------------------------------------------

export default function TransferOrderNew() {
   const navigate = useNavigate();
   const location = useLocation();

   const { pdvs: userPdvs, isLoading: loadingUserPdvs } = useUserPdvs();
   const { pdvs: allPdvs, isLoading: loadingAllPdvs } = useAllPdvs();

   const [originPdvId, setOriginPdvId] = useState('');
   const [destPdvId, setDestPdvId] = useState('');
   const [notes, setNotes] = useState('');
   const [items, setItems] = useState([]);

   const availability = useOriginAvailability(originPdvId);

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});

   // Origem e destino não podem coincidir — o backend recusa, e deixar a opção
   // no select só produziria um erro evitável.
   const destOptions = useMemo(
      () => allPdvs.filter((pdv) => String(pdv.id) !== String(originPdvId)),
      [allPdvs, originPdvId]
   );

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/inventory/transfers');
   };

   const hasValidItems =
      items.length > 0 &&
      items.every((item) => Number(item.requested_qtd) > 0);

   const hasRoute = !!originPdvId && !!destPdvId;

   // Itens acima do disponível — AVISO, não gate (D3).
   const overCount = useMemo(() => {
      if (!availability) return 0;
      return items.filter(
         (item) =>
            (availability[item.prod_id] ?? 0) - Number(item.requested_qtd || 0) <
            0
      ).length;
   }, [availability, items]);

   const canSubmit = hasRoute && hasValidItems && !isSubmitting;

   const totalUnits = items.reduce(
      (acc, item) => acc + (Number(item.requested_qtd) || 0),
      0
   );

   const pdvName = (list, id) => {
      const pdv = list.find((entry) => String(entry.id) === String(id));
      return pdv ? pdv.pdv_name ?? pdv.name ?? `PDV ${pdv.id}` : null;
   };
   const originName = pdvName(userPdvs, originPdvId);
   const destName = pdvName(allPdvs, destPdvId);

   const handleSubmit = async (event) => {
      event.preventDefault();
      if (!canSubmit) {
         if (items.length && !hasValidItems) {
            toast.error('Há item com quantidade zerada ou vazia na lista.');
         }
         return;
      }

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         const { order, message } = await createTransferOrder({
            originPdvId,
            destPdvId,
            notes: notes.trim(),
            items: items.map((item) => ({
               prodId: item.prod_id,
               requestedQtd: Number(item.requested_qtd),
            })),
         });

         toast.success(message);
         navigate(`/inventory/transfers/${order?.id ?? ''}`, { replace: true });
      } catch (err) {
         // FC4 §B.3: 422 com campo vai inline; o resto vira toast.
         setFieldErrors(err?.fieldErrors ?? {});
         notifyApiError(err, {
            fallback: 'Não foi possível criar a transferência.',
         });
      } finally {
         setIsSubmitting(false);
      }
   };

   const isLoading = loadingUserPdvs || loadingAllPdvs || isSubmitting;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <div style={{ marginBottom: 10 }}>
                  <GhostOnDarkButton type="button" onClick={handleBack}>
                     ← Voltar para a lista
                  </GhostOnDarkButton>
               </div>
               <PageTitle>Nova transferência</PageTitle>
               <PageSubtitle>
                  Cria a ordem e reserva o estoque na origem. Nada sai
                  fisicamente até a expedição.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            <ComposerPageGrid>
               <FormCard>
                  <Section>
                     <SectionHead>
                        <SectionIcon>
                           <FaRoute size={15} />
                           <span className="step">1</span>
                        </SectionIcon>
                        <SectionTitles>
                           <div className="t">Rota</div>
                           <div className="d">
                              De onde sai e para onde vai a mercadoria
                           </div>
                        </SectionTitles>
                     </SectionHead>

                     <FormGrid>
                        <Field $span={6}>
                           <FieldLabel htmlFor="origin-pdv">
                              Origem<span className="req">*</span>
                           </FieldLabel>
                           <FieldSelect
                              id="origin-pdv"
                              value={originPdvId}
                              disabled={isSubmitting}
                              $invalid={!!fieldErrors.origin_pdv_id}
                              onChange={(event) => {
                                 setOriginPdvId(event.target.value);
                                 if (event.target.value === destPdvId) {
                                    setDestPdvId('');
                                 }
                              }}
                           >
                              <option value="">Selecione</option>
                              {userPdvs.map((pdv) => (
                                 <option key={pdv.id} value={pdv.id}>
                                    {pdv.pdv_name ?? pdv.name ?? `PDV ${pdv.id}`}
                                 </option>
                              ))}
                           </FieldSelect>
                           {fieldErrors.origin_pdv_id ? (
                              <FieldError>
                                 {fieldErrors.origin_pdv_id}
                              </FieldError>
                           ) : (
                              <FieldHint>
                                 Apenas PDVs vinculados ao seu usuário.
                              </FieldHint>
                           )}
                        </Field>

                        <Field $span={6}>
                           <FieldLabel htmlFor="dest-pdv">
                              Destino<span className="req">*</span>
                           </FieldLabel>
                           <FieldSelect
                              id="dest-pdv"
                              value={destPdvId}
                              disabled={isSubmitting || !originPdvId}
                              $invalid={!!fieldErrors.dest_pdv_id}
                              onChange={(event) =>
                                 setDestPdvId(event.target.value)
                              }
                           >
                              <option value="">
                                 {originPdvId
                                    ? 'Selecione'
                                    : 'Escolha a origem primeiro'}
                              </option>
                              {destOptions.map((pdv) => (
                                 <option key={pdv.id} value={pdv.id}>
                                    {pdv.pdv_name ?? pdv.name ?? `PDV ${pdv.id}`}
                                 </option>
                              ))}
                           </FieldSelect>
                           {fieldErrors.dest_pdv_id ? (
                              <FieldError>{fieldErrors.dest_pdv_id}</FieldError>
                           ) : (
                              <FieldHint>
                                 Qualquer PDV da empresa, menos a origem.
                              </FieldHint>
                           )}
                        </Field>

                        <Field $span={12}>
                           <FieldLabel htmlFor="transfer-notes">
                              Observações
                           </FieldLabel>
                           <FieldTextarea
                              id="transfer-notes"
                              value={notes}
                              disabled={isSubmitting}
                              placeholder="Motivo da transferência, instruções para o destino"
                              onChange={(event) => setNotes(event.target.value)}
                           />
                           {fieldErrors.notes && (
                              <FieldError>{fieldErrors.notes}</FieldError>
                           )}
                        </Field>
                     </FormGrid>
                  </Section>

                  <Section>
                     <SectionHead>
                        <SectionIcon>
                           <FaBoxes size={15} />
                           <span className="step">2</span>
                        </SectionIcon>
                        <SectionTitles>
                           <div className="t">Itens</div>
                           <div className="d">
                              O que será reservado na origem
                           </div>
                        </SectionTitles>
                     </SectionHead>

                     <TransferItemsEditor
                        items={items}
                        onChange={setItems}
                        availabilityByProdId={availability}
                        disabled={isSubmitting}
                     />
                     {fieldErrors.items && (
                        <FieldError style={{ marginTop: 10 }}>
                           {fieldErrors.items}
                        </FieldError>
                     )}
                  </Section>
               </FormCard>

               <AsideCard>
                  <h2>Resumo</h2>

                  <AsideRow>
                     <span className="lbl">Rota</span>
                     <span className="val">
                        {originName && destName
                           ? `${originName} → ${destName}`
                           : '—'}
                     </span>
                  </AsideRow>
                  <AsideRow>
                     <span className="lbl">Itens</span>
                     <span className="val">{items.length}</span>
                  </AsideRow>
                  <AsideRow>
                     <span className="lbl">Unidades</span>
                     <span className="val">{totalUnits}</span>
                  </AsideRow>

                  <AsideChecklist>
                     <CheckLine ok={hasRoute}>
                        Origem e destino distintos
                     </CheckLine>
                     <CheckLine ok={hasValidItems}>Ao menos um item</CheckLine>
                     {/* Só afirma algo quando a posição da origem carregou —
                         sem dado, a linha não aparece (fail-open). */}
                     {availability != null && (
                        <CheckLine ok={overCount === 0}>
                           {overCount === 0
                              ? 'Quantidades dentro do disponível'
                              : `${overCount} item(ns) acima do disponível — permitido, mas o físico da origem pode ficar negativo`}
                        </CheckLine>
                     )}
                  </AsideChecklist>

                  <AsideActions>
                     <PrimaryButton type="submit" disabled={!canSubmit}>
                        {isSubmitting ? 'Criando…' : 'Criar transferência'}
                     </PrimaryButton>
                     <GhostButton
                        type="button"
                        onClick={handleBack}
                        disabled={isSubmitting}
                     >
                        Cancelar
                     </GhostButton>
                  </AsideActions>
               </AsideCard>
            </ComposerPageGrid>
         </form>
      </Container>
   );
}
